import { getRouteApi, useRouter } from "@tanstack/react-router";
import { useState, Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { MoreHorizontal, ChevronDown, FileText, ChevronRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { Card } from "@/components/ui/card";
import { ChartOptionsContextProvider } from "@/chartOptions/ChartOptionsContext";
import {
  useEquipmentInstallations, useEquipment, useEquipmentSiteVisits, useDeleteEquipment,
  type EquipmentSiteVisitData,
} from "@/equipment/api";
import { AudioWaveformPlayer } from "@/equipment/AudioWaveformPlayer";
import { FeatureFlag } from "@/featureFlags";
import { useLocation } from "@/location/api";
import { BellButton } from "@/components/snooze/BellButton";
import { SnoozeBanner } from "@/components/snooze/SnoozeBanner";
import { EquipmentSnoozeSheetWrapper } from "@/components/snooze/EquipmentSnoozeSheetWrapper";
import { useEquipmentSnooze, useDeleteEquipmentSnooze, useLocationSnooze } from "@/hooks/useSnooze";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { type AssetData, getShortEquipmentDetails, type InstallationData, isRefrigerant } from "@/installation/installation";
import { HealthSnapshotSection } from "@/refrigerant/HealthSnapshotSection";
import { getAssetTypeCharts } from "@/refrigerant/AssetCharts";
import { useOpportunities } from "@/opportunity/api";
import { EquipmentTotalPowerSection } from "@/equipment/EquipmentTotalPowerSection";

import {
  MobilePageHeader, StatusBadge, StatusDot, SegmentedControl, MetricCard,
  healthStatusToCopilot, HEALTH_TIME_RANGE_OPTIONS, CHART_TIME_RANGE_OPTIONS,
} from "@/components/copilot";
import { SiteVisitSheet } from "@/components/copilot/SiteVisitSheet";
import { IssueCard, type IssueData } from "@/components/copilot/IssueCard";
import { SummaryStrip } from "@/components/copilot/SummaryStrip";
import { useFeedbackState } from "@/components/copilot/useFeedbackState";
import { useOnSiteMode } from "@/components/copilot/useOnSiteMode";
import { OnSiteBar } from "@/components/copilot/OnSiteBar";
import { RecordSiteVisitTopSheet } from "@/components/copilot/TopSheet";

const route = getRouteApi(
  "/_authenticated/organization/$organizationId/_non_installer/locations/$locationId/equipment/$equipmentId/"
);

/* ───────── Helpers ───────── */

function formatRelative(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/* ───────── Site Visit Components ───────── */

const SiteVisitRow = ({ visit }: { visit: EquipmentSiteVisitData }) => {
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1 py-3 border-b border-[var(--orb-border)] last:border-b-0">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{visit.visited_by_name}</span>
        <time className="text-xs text-[var(--orb-text-secondary)]" dateTime={visit.created_at}>
          {new Date(visit.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
        </time>
      </div>
      {visit.notes && <p className="text-sm text-[var(--orb-text-secondary)] whitespace-pre-line">{visit.notes}</p>}
      {visit.recording_file && <div className="mt-1"><AudioWaveformPlayer src={visit.recording_file} /></div>}
      {visit.recording_transcript && (
        <div className="mt-0.5">
          <button onClick={() => setTranscriptOpen(!transcriptOpen)} className="inline-flex items-center gap-1 text-xs font-medium text-[var(--orb-text-tertiary)] hover:text-[var(--orb-text-secondary)] transition-colors">
            <ChevronRight size={12} className={`transition-transform ${transcriptOpen ? "rotate-90" : ""}`} />
            <FileText size={12} /> Transcript
          </button>
          {transcriptOpen && <p className="text-sm text-[var(--orb-text-secondary)] mt-1 pl-5 whitespace-pre-line">{visit.recording_transcript}</p>}
        </div>
      )}
    </div>
  );
};

const SiteVisitsList = ({ equipmentId }: { equipmentId: number }) => {
  const { data: { results: siteVisits } } = useEquipmentSiteVisits(equipmentId);
  return siteVisits.length === 0 ? (
    <p className="text-sm text-[var(--orb-text-tertiary)] py-4 text-center">No site visits recorded.</p>
  ) : (
    <div>{siteVisits.map((v: EquipmentSiteVisitData) => <SiteVisitRow key={v.id} visit={v} />)}</div>
  );
};

const ChartPlaceholder = ({ label }: { label: string }) => (
  <div className="bg-[var(--orb-gray-bg)] border-2 border-dashed border-[var(--orb-border)] rounded-xl p-6 aspect-video flex flex-col items-center justify-center text-center gap-2">
    <span className="text-2xl text-[var(--orb-text-tertiary)]">📊</span>
    <span className="text-sm font-medium text-[var(--orb-text-secondary)]">{label}</span>
    <span className="text-xs italic text-[var(--orb-text-tertiary)]">Tap data point to inspect · Pinch to zoom · Swipe to pan</span>
  </div>
);

/* ───────── Issue Card List (fetches real data) ───────── */

const IssueCardListInner = ({
  installationIds,
  isOnSite,
  feedback,
  getFeedback,
  setIssueFeedback,
  feedbackCounts,
}: {
  installationIds: number[];
  isOnSite: boolean;
  feedback: Record<string, any>;
  getFeedback: (id: string) => any;
  setIssueFeedback: (id: string, v: any) => void;
  feedbackCounts: any;
}) => {
  const { data: { results: opportunities } } = useOpportunities({
    filters: { installation_ids: installationIds, requires_action: true },
  });

  const issueDataList: IssueData[] = opportunities.map((opp) => ({
    id: String(opp.id),
    name: opp.name,
    activeSince: opp.identified_date ? formatRelative(opp.identified_date) : undefined,
    lastSeen: opp.last_detected_date ? formatRelative(opp.last_detected_date) + " ago" : undefined,
  }));

  const unreviewedCount = issueDataList.filter((iss) => !getFeedback(iss.id)).length;

  return (
    <>
      <div className="px-[var(--page-padding)] pt-4 pb-2 text-[13px] font-bold text-[var(--orb-text-secondary)] uppercase tracking-[0.5px]">
        Active Issues{isOnSite && unreviewedCount > 0 ? ` · ${unreviewedCount} to review` : ""}
      </div>
      {isOnSite && (
        <SummaryStrip counts={{ pending: unreviewedCount, confirmed: feedbackCounts.confirmed, "not-found": feedbackCounts["not-found"], "not-sure": feedbackCounts["not-sure"] }} />
      )}
      <div className="px-[var(--page-padding)]" data-card-container>
        {issueDataList.map((iss) => (
          <IssueCard
            key={iss.id}
            issue={iss}
            feedback={getFeedback(iss.id)}
            onFeedbackChange={setIssueFeedback}
            isOnSite={isOnSite}
          />
        ))}
      </div>
    </>
  );
};

/* ───────── Site Visit Sheet with real issues + feedback sync ───────── */

const SiteVisitSheetWithIssues = ({
  open, onOpenChange, equipmentName, installationIds, externalFeedback, onExternalFeedbackChange,
}: {
  open: boolean; onOpenChange: (open: boolean) => void; equipmentName: string; installationIds: number[];
  externalFeedback?: Record<string, any>; onExternalFeedbackChange?: (id: string, v: any) => void;
}) => {
  const { data: { results: opportunities } } = useOpportunities({
    filters: { installation_ids: installationIds, requires_action: true },
  });
  const activeIssues = opportunities.map((opp) => ({ id: String(opp.id), name: opp.name }));

  return (
    <SiteVisitSheet
      open={open} onOpenChange={onOpenChange} equipmentName={equipmentName}
      activeIssues={activeIssues} externalFeedback={externalFeedback} onExternalFeedbackChange={onExternalFeedbackChange}
    />
  );
};

/* ───────── Top Sheet with real issues + feedback sync ───────── */

const SiteVisitTopSheetWithIssues = ({
  open, onClose, equipmentName, installationIds, externalFeedback, onExternalFeedbackChange,
}: {
  open: boolean; onClose: () => void; equipmentName: string; installationIds: number[];
  externalFeedback?: Record<string, any>; onExternalFeedbackChange?: (id: string, v: any) => void;
}) => {
  const { data: { results: opportunities } } = useOpportunities({
    filters: { installation_ids: installationIds, requires_action: true },
  });
  const activeIssues = opportunities.map((opp) => ({ id: String(opp.id), name: opp.name }));

  return (
    <RecordSiteVisitTopSheet
      open={open} onClose={onClose} equipmentName={equipmentName}
      activeIssues={activeIssues} externalFeedback={externalFeedback} onExternalFeedbackChange={onExternalFeedbackChange}
    />
  );
};

/* ═══════════════════════════════════════════════
   MAIN PAGE — Unified layout, mode-toggled
   ═══════════════════════════════════════════════ */

const EquipmentDetailsPage = () => {
  const { equipmentId, organizationId, locationId } = route.useParams();
  const { data: equipmentData } = useEquipment(equipmentId);
  const { data: selectedLocation } = useLocation(locationId);
  const { data: { results: installations } } = useEquipmentInstallations(equipmentId);

  const { isOnSite, setMode: setOnSiteMode } = useOnSiteMode();
  const { feedback, setIssueFeedback, getFeedback, counts: feedbackCounts } = useFeedbackState();

  const [snoozeSheetOpen, setSnoozeSheetOpen] = useState(false);
  const [siteVisitDialogOpen, setSiteVisitDialogOpen] = useState(false);
  const [chartsExpanded, setChartsExpanded] = useState(!isOnSite);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [healthTimeRange, setHealthTimeRange] = useState(isOnSite ? "realtime" : "24h");
  const [chartType, setChartType] = useState("energy");
  const [chartTimeRange, setChartTimeRange] = useState("24h");

  const { mutate: deleteEquipment, isPending: isDeletePending } = useDeleteEquipment();
  const router = useRouter();

  // Snooze
  const { data: equipmentSnooze, isLoading: isEquipmentSnoozeLoading } = useEquipmentSnooze(equipmentId, equipmentData.is_effectively_snoozed);
  const shouldFetchLocationSnooze = equipmentData.is_effectively_snoozed && !equipmentSnooze && !isEquipmentSnoozeLoading && selectedLocation.is_snoozed;
  const { data: locationSnooze } = useLocationSnooze(selectedLocation.id, shouldFetchLocationSnooze);
  const activeSnooze = equipmentSnooze ?? (shouldFetchLocationSnooze ? locationSnooze : null);
  const isLocationLevelSnooze = !equipmentSnooze && !!locationSnooze && shouldFetchLocationSnooze;
  const deleteEquipSnooze = useDeleteEquipmentSnooze();
  const handleLiftSnooze = () => {
    if (!activeSnooze || isLocationLevelSnooze) return;
    deleteEquipSnooze.mutate({ equipmentId, snoozeId: activeSnooze.id }, { onSuccess: () => toast.success("Snooze lifted"), onError: () => toast.error("Failed to lift snooze") });
  };

  const installationIds = installations.map((i: InstallationData) => i.id);
  const modelDetails = equipmentData.equipment_model ? getShortEquipmentDetails(equipmentData.equipment_model as unknown as AssetData) : null;
  const copilotStatus = healthStatusToCopilot(equipmentData.health_status);

  const chartSummaryData: Record<string, string> = {
    energy: "Total Energy: 126 kWh", power: "Avg Power: 5.25 kW",
    voltage: "Avg Voltage: 238.4 V", current: "Avg Current: 22.1 A",
  };

  const handleDelete = () => {
    deleteEquipment(
      { equipment_id: equipmentData.id, sublocation_id: equipmentData.sublocation, location_id: selectedLocation.id },
      { onSuccess: () => { setDeleteDialogOpen(false); router.history.back(); }, onError: () => toast.error("Failed to delete equipment.") }
    );
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif" }}>

      {/* ── On-Site Bar (fixed at top, replaces nav bar when on-site) ── */}
      {isOnSite && (
        <>
          <OnSiteBar onRecordClick={() => setSiteVisitDialogOpen(true)} />
          <div style={{ height: 56 }} /> {/* spacer for fixed bar */}
        </>
      )}

      {/* ── Page Header ── */}
      <MobilePageHeader title={equipmentData.name} breadcrumb={selectedLocation.name} sticky={!isOnSite}>
        {modelDetails && <span className="text-xs font-medium text-[var(--orb-text-secondary)] mt-1 block">{modelDetails}</span>}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <StatusDot status={copilotStatus} />
          <StatusBadge status={copilotStatus === "neutral" ? "neutral" : copilotStatus}>
            {copilotStatus === "critical" ? "Critical" : copilotStatus === "warning" ? "Warning" : copilotStatus === "healthy" ? "No Issues" : "Unmonitored"}
          </StatusBadge>
          <BellButton isSnoozed={equipmentData.is_effectively_snoozed} onClick={(e) => { e.stopPropagation(); setSnoozeSheetOpen(true); }} />
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button className="w-9 h-9 rounded-full border border-[var(--orb-border)] flex items-center justify-center bg-white text-[var(--orb-text-secondary)] hover:bg-[var(--orb-gray-bg)] transition-colors" aria-label="More options">
                <MoreHorizontal size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSnoozeSheetOpen(true)}>Snooze notifications</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </MobilePageHeader>

      {/* ── Snooze Banner ── */}
      {equipmentData.is_effectively_snoozed && activeSnooze && (
        <div className="px-[var(--page-padding)] pt-3">
          <SnoozeBanner end={activeSnooze.end} onLift={handleLiftSnooze} isPending={deleteEquipSnooze.isPending} label={equipmentData.name} hideLift={isLocationLevelSnooze} subtitle={isLocationLevelSnooze ? "This snooze is from the location." : null} />
        </div>
      )}

      {/* ── Active Issues (IssueCard list with orbs) ── */}
      {installations.length > 0 && (
        <Suspense fallback={<div className="p-4"><Spinner /></div>}>
          <IssueCardListInner installationIds={installationIds} isOnSite={isOnSite} feedback={feedback} getFeedback={getFeedback} setIssueFeedback={setIssueFeedback} feedbackCounts={feedbackCounts} />
        </Suspense>
      )}

      {/* ── Health Overview ── */}
      <div className="px-[var(--page-padding)] pt-[var(--section-gap)]">
        <div className="flex flex-col gap-[var(--section-gap)]">
          <section>
            <span className="text-[var(--overline-size)] font-semibold tracking-[0.05em] uppercase text-[var(--orb-text-secondary)] block mb-2">EQUIPMENT</span>
            <SegmentedControl value={healthTimeRange} onValueChange={setHealthTimeRange} options={[...HEALTH_TIME_RANGE_OPTIONS]} className="mb-[var(--space-md)]" />
            <div className="grid grid-cols-2 gap-[var(--space-sm)]">
              <MetricCard label="Compressor Inlet" value={isOnSite ? "38" : "55.7"} unit="°F" expectedRange="Expected: 39–65 °F" outOfRange={isOnSite} />
              <MetricCard label="Compressor Outlet" value={isOnSite ? "149" : "128.8"} unit="°F" expectedRange="Expected: 147–173 °F" outOfRange={!isOnSite} />
              <MetricCard label={isOnSite ? "Vapor Liquid Coil" : "Total Energy"} value={isOnSite ? "104" : "126"} unit={isOnSite ? "°F" : "kWh"} />
              <MetricCard label={isOnSite ? "Ambient" : "Avg Cycle Duration"} value={isOnSite ? "91" : "59.9"} unit={isOnSite ? "°F" : "min"} />
            </div>
          </section>

          {/* Health snapshot for refrigerant */}
          {installations.length > 0 && installations[0] && isRefrigerant(installations[0]) && (
            <ChartOptionsContextProvider location={selectedLocation}>
              <div className="grid grid-cols-12 gap-3 w-full min-w-0">
                <HealthSnapshotSection selectedAsset={installations[0]} />
              </div>
            </ChartOptionsContextProvider>
          )}

          {/* Charts — collapsed on-site, visible remote */}
          {isOnSite ? (
            <section>
              <div className="bg-white border border-[var(--orb-border)] rounded-xl p-[var(--space-md)]">
                <button onClick={() => setChartsExpanded(!chartsExpanded)} className="flex items-center justify-between w-full" aria-expanded={chartsExpanded}>
                  <span className="text-[var(--heading-section-size)] font-bold">Monitoring Data</span>
                  <ChevronDown size={14} className={`text-[var(--orb-text-secondary)] transition-transform ${chartsExpanded ? "rotate-180" : ""}`} />
                </button>
                {chartsExpanded && (
                  <div className="mt-[var(--space-md)] flex flex-col gap-[var(--space-md)]">
                    <ChartPlaceholder label="Temperature Chart" />
                    <ChartPlaceholder label="Energy Chart" />
                  </div>
                )}
              </div>
            </section>
          ) : (
            <section>
              <h2 className="text-[var(--heading-section-size)] font-bold mb-[var(--space-sm)]">Real-time Data Charts</h2>
              <SegmentedControl value={chartType} onValueChange={setChartType} options={[{ value: "energy", label: "Energy" }, { value: "power", label: "Power" }, { value: "voltage", label: "Voltage" }, { value: "current", label: "Current" }]} className="mb-[var(--space-sm)]" />
              <SegmentedControl value={chartTimeRange} onValueChange={setChartTimeRange} options={[...CHART_TIME_RANGE_OPTIONS]} className="mb-[var(--space-md)]" />
              <div className="flex items-center gap-[var(--space-sm)] p-[var(--space-md)] bg-white border border-[var(--orb-border)] rounded-xl mb-[var(--space-sm)]">
                <span className="text-[var(--heading-section-size)] font-bold">{chartSummaryData[chartType]}</span>
                <span className="text-sm font-medium text-[var(--orb-text-secondary)]">— normal range →</span>
              </div>
              <ChartPlaceholder label={`${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart`} />
              {installations.length > 0 && (
                <ChartOptionsContextProvider location={selectedLocation}>
                  <div className="mt-[var(--section-gap)]">{getAssetTypeCharts(installations[0], organizationId)}</div>
                </ChartOptionsContextProvider>
              )}
            </section>
          )}

          {/* Site Visit History */}
          <FeatureFlag name="site_visits">
            <section>
              <h2 className="text-[var(--heading-section-size)] font-bold mb-[var(--space-sm)]">Site Visit History</h2>
              <ErrorBoundary fallback={<p className="text-sm text-[var(--orb-text-tertiary)] text-center">Unable to load.</p>}>
                <Suspense fallback={<Spinner />}><SiteVisitsList equipmentId={equipmentId} /></Suspense>
              </ErrorBoundary>
            </section>
          </FeatureFlag>
        </div>
      </div>

      {/* ── Snooze Sheet ── */}
      <EquipmentSnoozeSheetWrapper equipmentId={equipmentId} equipmentName={equipmentData.name} isSnoozed={equipmentData.is_effectively_snoozed} open={snoozeSheetOpen} onOpenChange={setSnoozeSheetOpen} locationId={selectedLocation.id} locationSnoozed={selectedLocation.is_snoozed} />

      {/* ── Record Site Visit — Top Sheet ── */}
      <Suspense fallback={null}>
        <SiteVisitTopSheetWithIssues
          open={siteVisitDialogOpen}
          onClose={() => setSiteVisitDialogOpen(false)}
          equipmentName={equipmentData.name}
          installationIds={installationIds}
          externalFeedback={feedback}
          onExternalFeedbackChange={setIssueFeedback}
        />
      </Suspense>

      {/* ── Delete Dialog ── */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteConfirmText(""); }}>
        <DialogContent className="max-w-md w-[90vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>Delete equipment</DialogTitle>
            <DialogDescription>This will permanently delete {equipmentData.name}. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 py-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-[var(--orb-text-secondary)]">Type <span className="font-semibold text-[var(--orb-text-primary)]">permanently delete</span> to confirm</span>
              <input className="border border-[var(--orb-border)] rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--orb-purple)]" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="permanently delete" autoFocus />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isDeletePending}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteConfirmText !== "permanently delete" || isDeletePending}>
              {isDeletePending && <Spinner className="mr-2 h-3 w-3" />} Delete equipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentDetailsPage;
